import React, { useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import './styles/mui-rtl-fix.css';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import MuiDateProviders from './components/providers/MuiDateProviders';
import ProtectedRoute from './components/auth/ProtectedRoute';
import PublicRoute from './components/auth/PublicRoute';
import Layout from './components/layout/Layout';
import Login from './pages/auth/Login';
import Dashboard from './pages/Dashboard';
import Shipments from './pages/Shipments';
import ShipmentDetail from './pages/ShipmentDetail';
import CreateShipment from './pages/CreateShipment';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import CreateClient from './pages/CreateClient';
import Drivers from './pages/Drivers';
import DriverDetail from './pages/DriverDetail';
import Vehicles from './pages/Vehicles';
import VehicleDetail from './pages/VehicleDetail';
import ShipmentCosting from './pages/ShipmentCosting';

import Accounting from './pages/Accounting';
import Quotations from './pages/Quotations';
import Vendors from './pages/Vendors';
import Users from './pages/Users';
import Settings from './pages/Settings';

import TrackingPage from './pages/TrackingPage';
import NotFound from './pages/NotFound';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SuperAdminCompanies from './pages/SuperAdminCompanies';
import ArabicPDFExample from './components/ArabicPDFExample';
import LandingPage from './pages/LandingPage';

// Marketing Pages
import MarketingLayout from './components/marketing/MarketingLayout';
import HomePage from './pages/marketing/HomePage';
import HomePageArabic from './pages/marketing/HomePageArabic';
import FeaturesPage from './pages/marketing/FeaturesPage';
import FeaturesPageArabic from './pages/marketing/FeaturesPageArabic';
import ContactPage from './pages/marketing/ContactPage';
import ContactPageArabic from './pages/marketing/ContactPageArabic';
import PricingPage from './pages/marketing/PricingPage';
import PricingPageArabic from './pages/marketing/PricingPageArabic';
import TripCostingPage from './pages/marketing/TripCostingPage';
import TripCostingPageArabic from './pages/marketing/TripCostingPageArabic';

function App() {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const direction = currentLanguage?.startsWith('ar') ? 'rtl' : 'ltr';

  useLayoutEffect(() => {
    // Set document direction based on language
    document.documentElement.dir = direction;
    document.documentElement.lang = currentLanguage;
    document.body.setAttribute('dir', direction);
    document.body.setAttribute('lang', currentLanguage);
  }, [currentLanguage, direction]);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <MuiDateProviders>
            <Router>
          <div className="min-h-screen bg-gray-50" dir={direction}>
            <Routes>
              {/* Marketing Routes - Arabic as Default */}
              <Route 
                path="/" 
                element={
                  <MarketingLayout>
                    <HomePageArabic />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/ar" 
                element={<Navigate to="/" replace />} 
              />
              <Route 
                path="/ar/features" 
                element={
                  <MarketingLayout>
                    <FeaturesPageArabic />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/ar/contact" 
                element={
                  <MarketingLayout>
                    <ContactPageArabic />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/ar/pricing" 
                element={
                  <MarketingLayout>
                    <PricingPageArabic />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/ar/trip-costing" 
                element={
                  <MarketingLayout>
                    <TripCostingPageArabic />
                  </MarketingLayout>
                } 
              />
              
              {/* English Marketing Routes */}
              <Route 
                path="/en" 
                element={
                  <MarketingLayout>
                    <HomePage />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/en/features" 
                element={
                  <MarketingLayout>
                    <FeaturesPage />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/en/contact" 
                element={
                  <MarketingLayout>
                    <ContactPage />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/en/pricing" 
                element={
                  <MarketingLayout>
                    <PricingPage />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/en/trip-costing" 
                element={
                  <MarketingLayout>
                    <TripCostingPage />
                  </MarketingLayout>
                } 
              />
              
              {/* SEO Redirect Routes - Fix 404 Issues */}
              {/* Redirect old /modules/ URLs to appropriate marketing pages */}
              <Route path="/modules/dispatch" element={<Navigate to="/features" replace />} />
              <Route path="/modules/dispatch/" element={<Navigate to="/features" replace />} />
              <Route path="/modules/trip-costing" element={<Navigate to="/trip-costing" replace />} />
              <Route path="/modules/trip-costing/" element={<Navigate to="/trip-costing" replace />} />
              <Route path="/modules/maintenance" element={<Navigate to="/features" replace />} />
              <Route path="/modules/maintenance/" element={<Navigate to="/features" replace />} />
              
              {/* Arabic module redirects */}
              <Route path="/ar/modules/dispatch" element={<Navigate to="/ar/features" replace />} />
              <Route path="/ar/modules/dispatch/" element={<Navigate to="/ar/features" replace />} />
              <Route path="/ar/modules/trip-costing" element={<Navigate to="/ar/trip-costing" replace />} />
              <Route path="/ar/modules/trip-costing/" element={<Navigate to="/ar/trip-costing" replace />} />
              <Route path="/ar/modules/maintenance" element={<Navigate to="/ar/features" replace />} />
              <Route path="/ar/modules/maintenance/" element={<Navigate to="/ar/features" replace />} />
              
              {/* Fix missing about page redirects */}
              <Route path="/about" element={<Navigate to="/features" replace />} />
              <Route path="/ar/about" element={<Navigate to="/ar/features" replace />} />
              <Route path="/about/" element={<Navigate to="/features" replace />} />
              <Route path="/ar/about/" element={<Navigate to="/ar/features" replace />} />
              
              {/* Legacy Routes for Backward Compatibility */}
              <Route 
                path="/features" 
                element={
                  <MarketingLayout>
                    <FeaturesPage />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/contact" 
                element={
                  <MarketingLayout>
                    <ContactPage />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/pricing" 
                element={
                  <MarketingLayout>
                    <PricingPage />
                  </MarketingLayout>
                } 
              />
              <Route 
                path="/trip-costing" 
                element={
                  <MarketingLayout>
                    <TripCostingPage />
                  </MarketingLayout>
                } 
              />
              
              {/* Public Routes */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route path="/track/:trackingId?" element={<TrackingPage />} />

              {/* Super Admin Routes */}
              <Route
                path="/super-admin/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SuperAdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/super-admin/companies"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SuperAdminCompanies />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route 
                path="/app" 
                element={<Navigate to="/dashboard" replace />} 
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute module="dashboard">
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shipments"
                element={
                  <ProtectedRoute module="shipments">
                    <Layout>
                      <Shipments />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shipments/create"
                element={
                  <ProtectedRoute module="shipments">
                    <Layout>
                      <CreateShipment />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shipments/:id"
                element={
                  <ProtectedRoute module="shipments">
                    <Layout>
                      <ShipmentDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shipments/:id/edit"
                element={
                  <ProtectedRoute module="shipments">
                    <Layout>
                      <CreateShipment />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients"
                element={
                  <ProtectedRoute module="clients">
                    <Layout>
                      <Clients />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients/create"
                element={
                  <ProtectedRoute module="clients">
                    <Layout>
                      <CreateClient />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients/:id"
                element={
                  <ProtectedRoute module="clients">
                    <Layout>
                      <ClientDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/clients/:id/edit"
                element={
                  <ProtectedRoute module="clients">
                    <Layout>
                      <CreateClient />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/drivers"
                element={
                  <ProtectedRoute module="drivers">
                    <Layout>
                      <Drivers />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/drivers/:id"
                element={
                  <ProtectedRoute module="drivers">
                    <Layout>
                      <DriverDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vehicles"
                element={
                  <ProtectedRoute module="vehicles">
                    <Layout>
                      <Vehicles />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vehicles/:id"
                element={
                  <ProtectedRoute module="vehicles">
                    <Layout>
                      <VehicleDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/accounting"
                element={
                  <ProtectedRoute module="accounting">
                    <Layout>
                      <Accounting />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/quotations"
                element={
                  <ProtectedRoute module="quotation">
                    <Layout>
                      <Quotations />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vendors"
                element={
                  <ProtectedRoute module="vendors">
                    <Layout>
                      <Vendors />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <ProtectedRoute module="users">
                    <Layout>
                      <Users />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute module="settings">
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/arabic-pdf-example"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ArabicPDFExample />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Fallback Routes */}
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </div>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                style: {
                  background: '#10b981',
                },
              },
              error: {
                duration: 5000,
                style: {
                  background: '#ef4444',
                },
              },
            }}
          />
            </Router>
          </MuiDateProviders>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
